import { fileHref } from '../lib/fileAccess'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { DRILL_TYPES, drillByLabel } from '../lib/drillTypes'
import { shipTodayAuto } from '../lib/shipTime'

export default function DrillLog({ onNavigate }) {
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ drill_type: '', drill_date: shipTodayAuto(), crew_present: '', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await supabase.from('drill_records').select('*').eq('client_id', config.id).order('drill_date', { ascending: false })
      setRows(data || [])
    } catch (e) { /* ignore */ }
    setLoading(false)
  }, [config])

  useEffect(() => { if (config?.id) load() }, [config, load])

  async function saveEntry() {
    if (!form.drill_type || !form.drill_date) return
    setSaving(true)
    const d = drillByLabel(form.drill_type)
    await supabase.from('drill_records').insert({
      client_id: config.id,
      drill_type: form.drill_type,
      drill_date: form.drill_date,
      crew_present: form.crew_present.trim() || null,
      notes: form.notes.trim() || null,
      sms_ref: d ? d.sms_ref : null,
      code_ref: d ? d.code_ref : null,
    })
    setSaving(false)
    setShowAdd(false)
    setForm({ drill_type: '', drill_date: shipTodayAuto(), crew_present: '', notes: '' })
    load()
  }

  async function del(r) {
    const label = 'drill record ' + [r.drill_type, r.drill_date].filter(Boolean).join(' ')
    if (!confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'drill_records', id: r.id, label, onUndone: () => load() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) }
    load()
  }

  const fmt = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) } catch { return d } }

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {onNavigate && <button onClick={() => onNavigate('logbooks')} style={btnGhost}>&larr; Log Books</button>}
          <h2 style={{ fontSize:'1.3rem', fontWeight:600, color:'var(--text)', margin:0 }}>Drill Log</h2>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {onNavigate && <button onClick={() => onNavigate('drill_matrix')} style={btnOutline}>View Drill Matrix</button>}
          <button onClick={() => setShowAdd(true)} style={btnOutline}>+ Add manually</button>
          <button onClick={() => { localStorage.setItem('hc_open_checklist', 'SMS-15 Drill Report'); localStorage.setItem('hc_page', 'checklists'); if (onNavigate) onNavigate('checklists') }} style={btnGold}>New Drill Report</button>
        </div>
      </div>

      {loading ? (
        <p style={{ color:'var(--muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={{ padding:'2.5rem', textAlign:'center', color:'var(--muted)', border:'1px solid var(--border)', borderRadius:12 }}>
          No drills recorded yet. Completing an SMS-15 Drill Report adds records here automatically, or add one manually.
        </div>
      ) : (
        <div style={{ border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
          {rows.map((r, idx) => (
            <div key={r.id} style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'14px 16px', borderBottom: idx < rows.length-1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ minWidth:96, fontSize:13, fontWeight:600, color:'var(--text)' }}>{fmt(r.drill_date)}</div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'var(--gold2)' }}>{r.drill_type}</span>
                  {r.sms_ref && <span style={{ fontSize:11, color:'var(--muted)' }}>{r.sms_ref}</span>}
                  {r.code_ref && <span style={{ fontSize:11, color:'var(--muted)' }}>· {r.code_ref}</span>}
                </div>
                {r.crew_present && <div style={{ fontSize:12, color:'var(--muted)', marginTop:3 }}>Crew: {r.crew_present}</div>}
                {r.notes && <div style={{ fontSize:12, color:'var(--text)', marginTop:3, opacity:.85 }}>{r.notes}</div>}
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {r.pdf_url && <a href={fileHref(r.pdf_url)} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'#2a6a96', textDecoration:'none' }}>PDF</a>}
                <button onClick={() => del(r)} style={{ ...btnGhost, color:'#b4313c', padding:'4px 8px' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div style={overlay} onClick={() => !saving && setShowAdd(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize:'1.1rem', fontWeight:600, color:'var(--text)', margin:'0 0 16px' }}>Add drill record</h3>
            <label style={lbl}>Drill type</label>
            <select style={inp} value={form.drill_type} onChange={e => setForm(f => ({ ...f, drill_type: e.target.value }))}>
              <option value="">Select drill…</option>
              {DRILL_TYPES.map(d => <option key={d.key} value={d.label}>{d.label}</option>)}
            </select>
            <label style={lbl}>Date</label>
            <input type="date" style={inp} value={form.drill_date} onChange={e => setForm(f => ({ ...f, drill_date: e.target.value }))} />
            <label style={lbl}>Crew present</label>
            <input style={inp} placeholder="Names, comma separated" value={form.crew_present} onChange={e => setForm(f => ({ ...f, crew_present: e.target.value }))} />
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, minHeight:64, resize:'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16 }}>
              <button onClick={() => setShowAdd(false)} style={btnGhost} disabled={saving}>Cancel</button>
              <button onClick={saveEntry} style={btnGold} disabled={saving || !form.drill_type}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const btnGold = { background:'var(--gold)', color:'#1a1a1a', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }
const btnOutline = { background:'transparent', color:'var(--text)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:500, cursor:'pointer' }
const btnGhost = { background:'transparent', color:'var(--muted)', border:'none', borderRadius:8, padding:'6px 10px', fontSize:13, cursor:'pointer' }
const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }
const modal = { background:'var(--card, #1a1f2b)', border:'1px solid var(--border)', borderRadius:14, padding:24, width:'100%', maxWidth:440 }
const lbl = { display:'block', fontSize:12, fontWeight:600, color:'var(--muted)', margin:'12px 0 4px' }
const inp = { width:'100%', boxSizing:'border-box', background:'var(--bg, #0f131a)', border:'1px solid var(--border)', borderRadius:8, padding:'9px 11px', fontSize:13, color:'var(--text)' }
